import { useEffect, useState } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { convertDoubleToCurrencyPTBR } from "../../../../../utils/ConverterUtils";
import { formatDate } from "../../../../../utils/DateUtils";
import { ApiError } from "../../../Common/ApiError";
import { successAlert } from "../../../alert/defaultAlert";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import LoadingCenter from "../../../Common/LoadingCenter";
import EmployeePaymentFields, { useEmployeePaymentFields } from "./EmployeePaymentFields";
import EmployeePaymentBreakdown from "./EmployeePaymentBreakdown";
import "./EmployeePaymentModal.css";

/**
 * Registra o pagamento de um colaborador a partir de uma linha da tela Comissões.
 *
 * O valor mostrado vem da prévia do servidor, e não da conta da tela: a tela permite simular o
 * pote, e o caixa precisa receber o valor oficial. Ao confirmar, o servidor recalcula de novo e
 * gera a conta a pagar já baixada no Caixa Financeiro da filial.
 *
 * @param {boolean} show - Controla a abertura do modal.
 * @param {function} setShow - Fecha o modal.
 * @param {object} target - Linha paga: { employeeId, employeeName, branchId, branchName }.
 * @param {object} period - { startDate, endDate } no formato YYYY-MM-DD, o filtro da tela.
 * @param {number} scopeBranchId - Filial do cabeçalho, nula em "Todas as filiais".
 * @param {boolean} isSimulating - A tela está com base ou percentual do pote alterados.
 * @param {function} onSaved - Chamado depois de registrar, para a tela recarregar.
 */
const EmployeePaymentModal = ({ show, setShow, target, period, scopeBranchId, isSimulating, onSaved }) => {

    const { t } = useTranslation();
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fields = useEmployeePaymentFields();
    const { reset } = fields;

    // Cada abertura busca a prévia de novo: um vale lançado entre uma abertura e outra muda o
    // valor.
    useEffect(() => {
        if (!show || !target) {
            return;
        }

        setPreview(null);
        reset();
        setLoading(true);

        axiosInstance.get(`${Api.cash_flow.employee_payment}/preview`, {
            params: {
                employeeId: target.employeeId,
                branchId: target.branchId,
                startDate: period.startDate,
                endDate: period.endDate,
                scopeBranchId
            }
        })
            .then(({ data }) => setPreview(data))
            .catch((error) => {
                setShow(false);
                ApiError(error);
            })
            .finally(() => setLoading(false));
    }, [show, target, period, scopeBranchId, setShow, reset]);

    const overlapping = preview?.overlapping || [];
    const netValue = Number(preview?.netValue || 0);
    // Período já pago ou líquido sem valor: o servidor recusaria, então o botão nem habilita.
    const blocked = !preview || overlapping.length > 0 || netValue <= 0;

    /** Confere os campos e envia; os valores nunca vão no corpo, o servidor recalcula. */
    const confirm = () => {
        if (blocked || !fields.validate()) {
            return;
        }

        setIsSubmitting(true);
        axiosInstance.post(Api.cash_flow.employee_payment, {
            employee: { id: target.employeeId },
            branch: { id: target.branchId },
            periodStart: period.startDate,
            periodEnd: period.endDate,
            scopeBranchId,
            ...fields.toPayload()
        })
            .then(() => {
                fields.remember();
                setShow(false);
                successAlert(t('message.operation_successfully'), () => onSaved?.());
            })
            .catch((error) => ApiError(error))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <ModalPageEvent
            show={show}
            setShow={setShow}
            centered={true}
            title={t('cash_flow.employee_payment_register')}
            btnText={t('confirm')}
            btnOnClick={confirm}
            btnDisabled={isSubmitting || loading || blocked}
            child={loading || !preview ? <LoadingCenter /> : (
                <Row className="g-3">
                    <Col xs={12}>
                        <div className="employee-payment__header">
                            <strong>{preview.employee?.name || target?.employeeName}</strong>
                            <span>
                                {target?.branchName ? `${target.branchName} · ` : ''}
                                {t('cash_flow.employee_payment_period_range', { start: formatDate(period.startDate), end: formatDate(period.endDate) })}
                            </span>
                        </div>
                    </Col>

                    <Col xs={12}>
                        <EmployeePaymentBreakdown values={preview} />
                    </Col>

                    {isSimulating && (
                        <Col xs={12}>
                            <Alert variant="info" className="employee-payment__alert">
                                <i className="fa-solid fa-circle-info me-2"></i>
                                {t('cash_flow.employee_payment_simulation_hint')}
                            </Alert>
                        </Col>
                    )}

                    {overlapping.length > 0 && (
                        <Col xs={12}>
                            <Alert variant="warning" className="employee-payment__alert">
                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                {t('cash_flow.employee_payment_overlap')}
                                <ul className="employee-payment__overlap-list">
                                    {overlapping.map(payment => (
                                        <li key={payment.id}>
                                            {t('cash_flow.employee_payment_period_range', { start: formatDate(payment.periodStart), end: formatDate(payment.periodEnd) })}
                                            {' · '}{convertDoubleToCurrencyPTBR(payment.netValue)}
                                            {' · '}{t('cash_flow.employee_payment_paid_on', { date: formatDate(payment.paymentDate) })}
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        </Col>
                    )}

                    {overlapping.length === 0 && netValue <= 0 && (
                        <Col xs={12}>
                            <Alert variant="warning" className="employee-payment__alert">
                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                {t('cash_flow.employee_payment_net_not_positive')}
                            </Alert>
                        </Col>
                    )}

                    {!blocked && <EmployeePaymentFields fields={fields} />}
                </Row>
            )}
        />
    );
};

export default EmployeePaymentModal;
